import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { usePantryNavigation } from '../hooks/usePantryNavigation'
import { useUnlinkPosState } from '../hooks/useUnlinkPosState'
import { useWarehouses } from '../hooks/useWarehouses'
import type { PantryNavApiResponse } from '../types/pantryNavigation'
import type { WHLocation } from '../types/warehouse'

export interface PantryStore {
  storeId: string
  store_name: string
  // Auth normalizes the API's snake_case `store_name` into camelCase
  // `storeName` on the active store; the Store Settings connection card reads it.
  storeName?: string
  storeType: string
  storeUrl: string
  // Storefront preview screenshot rendered by the Store Settings connection card.
  screenshotUrl?: string
  // Whether product images sync from the POS; toggled by the Store Settings
  // "Sync images from POS" panel. Sourced from the API's `sync_parent_images`.
  syncParentImages?: boolean
  orgId: string
  // Source: frontend/src/modules/auth/utils/index.ts:67 — auth normalizes
  // the API's snake_case `company_id` into `companyId` on every store
  // object. Needed by the Category Mapping tab to call the Cova
  // pre-onboarding classifications endpoints.
  companyId?: string | number | null
  // Greenline multi-company ids (`company_ids` on the store). Mapped by auth
  // next to `isPantry`; non-empty means the store went through add-company and
  // link-pos must send `one_store_manny_companies: true`.
  companyIds?: Array<string | number>
  csWsId?: string
  isPantry?: boolean
  // Whether Greenline computes tax per-location (Tax V2) vs. off the
  // product's Greenline tax class (Tax V1); toggled by the Store Settings
  // Greenline Tax V2 panel. Sourced from the API's
  // `pos_greenline_using_tax_v2` via the auth store projection + mapAuthState
  // (see MINE-0005) — only meaningful for Greenline stores.
  posGreenlineUsingTaxV2?: boolean
  enableRevampUIForPantryStore?: boolean
  authenticated?: boolean
  syncCompleted?: boolean
  useMockData?: boolean
  [key: string]: unknown
}

interface PantryContextValue {
  store: PantryStore | null
  warehouses: WHLocation[]
  pos_locations_map: Record<string, WHLocation>
  warehouses_map: Record<string, WHLocation>
  refreshStore: () => void
  navData: PantryNavApiResponse | null
  isNavLoading: boolean
  canSwitchWorkspace: boolean
  // True only when the unlink-POS flag is on AND the active store either has no
  // POS company or is a mock-data store (`useMockData`). Gates the manual product
  // create/edit feature — real POS-connected stores keep their previous flow
  // unless they are mock-data stores.
  isUnlinkPos: boolean
}

const noop = () => {}

const PantryContext = createContext<PantryContextValue>({
  store: null,
  warehouses: [],
  pos_locations_map: {},
  warehouses_map: {},
  refreshStore: noop,
  navData: null,
  isNavLoading: false,
  canSwitchWorkspace: false,
  isUnlinkPos: false,
})

interface PantryProviderProps {
  store: PantryStore | null | undefined
  // Fetch nav + warehouses. Defaults to `store.isPantry`; the host app turns
  // it on for non-pantry POS stores rendered with the Pantry UI.
  enabled?: boolean
  onRefreshStore?: () => void
  children: ReactNode
}

const PantryProviderInner = ({
  store,
  enabled,
  onRefreshStore,
  children,
}: PantryProviderProps) => {
  const storeId = store?.storeId ?? ''
  const organizationId = store?.orgId
  const isEnabled = enabled ?? !!store?.isPantry

  // A store "has a POS company" when companyId is a real, non-zero value —
  // missing / 0 / '0' / '' / null / undefined all mean it cannot connect to a POS.
  const unlinkPosFlag = useUnlinkPosState()
  const hasCompanyId = !!store?.companyId && String(store.companyId) !== '0'
  const useMockData = store?.useMockData === true
  // Unlink-POS only when the flag is on AND the store either has no POS company
  // or is a mock-data store — mock stores use the manual flow even with a real
  // companyId, while real POS-connected stores keep their previous flow.
  const isUnlinkPos = unlinkPosFlag && (!hasCompanyId || useMockData)

  // In unlink-POS state, include pantry warehouses without a `pos_id` so the manual
  // product create flow can build one location per warehouse.
  const { data: warehouses = [] } = useWarehouses(storeId, organizationId, {
    enabled: isEnabled,
    includeUnlinkPos: isUnlinkPos,
  })

  const { data: navData, isLoading: isNavLoading } = usePantryNavigation(
    storeId,
    { enabled: isEnabled },
  )

  const value = useMemo<PantryContextValue>(() => {
    const pos_locations_map: Record<string, WHLocation> = {}
    const warehouses_map: Record<string, WHLocation> = {}
    for (const wh of warehouses) {
      if (wh.id) pos_locations_map[wh.id] = wh
      if (wh.warehouse_id) warehouses_map[wh.warehouse_id] = wh
    }
    return {
      store: store ?? null,
      warehouses,
      pos_locations_map,
      warehouses_map,
      refreshStore: onRefreshStore ?? noop,
      navData: navData ?? null,
      isNavLoading,
      canSwitchWorkspace: navData?.can_switch_workspace === true,
      isUnlinkPos,
    }
  }, [store, warehouses, onRefreshStore, navData, isNavLoading, isUnlinkPos])

  return (
    <PantryContext.Provider value={value}>{children}</PantryContext.Provider>
  )
}

export const PantryProvider = ({
  store,
  enabled,
  onRefreshStore,
  children,
}: PantryProviderProps) => (
  <PantryProviderInner
    store={store}
    enabled={enabled}
    onRefreshStore={onRefreshStore}
  >
    {children}
  </PantryProviderInner>
)

export const usePantryStore = (): PantryStore | null =>
  useContext(PantryContext).store

export const usePantryWarehouses = (): WHLocation[] =>
  useContext(PantryContext).warehouses

export const usePantryPosLocationsMap = (): Record<string, WHLocation> =>
  useContext(PantryContext).pos_locations_map

export const usePantryWarehousesMap = (): Record<string, WHLocation> =>
  useContext(PantryContext).warehouses_map

export const usePantryRefreshStore = (): (() => void) =>
  useContext(PantryContext).refreshStore

export const usePantryNavData = (): PantryNavApiResponse | null =>
  useContext(PantryContext).navData

export const usePantryNavLoading = (): boolean =>
  useContext(PantryContext).isNavLoading

export const usePantryCanSwitchWorkspace = (): boolean =>
  useContext(PantryContext).canSwitchWorkspace

// True only when the unlink-POS flag is on AND the active store either has no POS
// company or is a mock-data store (`useMockData`). Product uses this instead of the
// raw flag to gate the manual create/edit feature — real POS-connected stores keep
// their previous flow unless they are mock-data stores.
export const usePantryIsUnlinkPos = (): boolean =>
  useContext(PantryContext).isUnlinkPos
